import { animate, style, transition, trigger } from '@angular/animations';
import { Platform } from '@angular/cdk/platform';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    inject,
    input,
    OnDestroy,
    OnInit,
    output,
} from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { APP_TOAST_DURATION } from '@constants/app.constant';
import { Toast } from '@models/common.model';
import {
    debounceTime,
    filter,
    map,
    startWith,
    Subject,
    takeUntil,
    timer,
    withLatestFrom,
} from 'rxjs';

@Component({
    selector: 'pn-toast-item',
    templateUrl: './toast-item.component.html',
    styleUrl: './toast-item.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('showState', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(-100%)' }),
                animate(
                    '500ms cubic-bezier(0.35, 0, 0.25, 1)',
                    style({ opacity: '*', transform: '*' })
                ),
            ]),
        ]),
    ],
})
export class ToastItemComponent implements OnInit, AfterViewInit, OnDestroy {
    public toast = input.required<Toast>();
    public index = input<number>();
    public handleDismiss = output<{ index: number; toast: Toast }>();
    private _destroyed = new Subject<void>();
    private readonly _isBrowser = inject(Platform).isBrowser;
    private readonly routerStart$ = this._router.events.pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        map(() => true),
        startWith(false)
    );
    private readonly routerEnd$ = this._router.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    );

    constructor(private readonly _router: Router) {}

    ngOnInit(): void {
        this.routerEnd$
            .pipe(
                withLatestFrom(this.routerStart$),
                filter(([_, routerStart]) => !!this.toast().closeOnNavigation && routerStart),
                takeUntil(this._destroyed)
            )
            .subscribe(() => {
                this.handleDismiss.emit({
                    index: this.index() as number,
                    toast: this.toast(),
                });
            });
    }

    ngAfterViewInit(): void {
        this._initTimer();
    }

    /**
     * Init timer
     *
     * @private
     * @return {*}
     * @memberof ToastItemComponent
     */
    private _initTimer(): void {
        if (this.toast().sticky || !this._isBrowser || this.toast().closeOnNavigation) {
            return;
        }
        this._destroyed = new Subject<void>();
        timer(this.toast().duration || APP_TOAST_DURATION)
            .pipe(debounceTime(0), takeUntil(this._destroyed))
            .subscribe(() => {
                this.handleDismiss.emit({
                    index: this.index() as number,
                    toast: this.toast(),
                });
            });
    }

    /**
     * Unsubscribe timer
     *
     * @private
     * @memberof ToastItemComponent
     */
    private _unsubscribe() {
        this._destroyed.next();
        this._destroyed.complete();
    }

    /**
     * Handle clear
     *
     * @memberof ToastItemComponent
     */
    public handleClear() {
        this._unsubscribe();
        this.handleDismiss.emit({
            index: this.index() as number,
            toast: this.toast(),
        });
    }

    /**
     * Handle mouse enter
     *
     * @memberof ToastItemComponent
     */
    public handleMouseEnter() {
        this._unsubscribe();
    }

    /**
     * Handle mouse leave
     *
     * @memberof ToastItemComponent
     */
    public handleMouseLeave() {
        this._initTimer();
    }

    /**
     * Handle action
     *
     * @memberof ToastItemComponent
     */
    public handleAction() {
        this.toast().action?.fn();
        this.handleClear();
    }

    ngOnDestroy(): void {
        this._unsubscribe();
    }
}
